import * as THREE from 'three';
import type { Renderer } from '../base/Renderer';
import type { SimulationState, Coordinate } from '../../core/types/SimulationTypes';

export class ThreeJSRenderer implements Renderer {
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private sandMesh: THREE.Mesh | null = null;
  private waterMeshes: THREE.Mesh[] = [];
  private sidePlanes: THREE.Group | null = null;
  private container: HTMLElement | null = null;

  private gridWidth: number = 0;
  private gridHeight: number = 0;

  private isDragging: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private cameraAngle: number = Math.PI / 4;
  private cameraDistance: number = 150;
  private cameraHeight: number = 100;

  public initialize(container: HTMLElement): void {
    this.container = container;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x2a2a2a);

    this.camera = new THREE.PerspectiveCamera(
      50,
      container.clientWidth / container.clientHeight,
      1,
      1000
    );

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    container.appendChild(this.renderer.domElement);

    // Warm ambient light for natural look
    const ambientLight = new THREE.AmbientLight(0xffffee, 0.7);
    this.scene.add(ambientLight);

    // Warm sunlight for realistic sand rendering
    const directionalLight = new THREE.DirectionalLight(0xffffcc, 1.0);
    directionalLight.position.set(50, 100, 50);
    this.scene.add(directionalLight);

    window.addEventListener('resize', this.handleResize);
    this.renderer.domElement.addEventListener('mousedown', this.handleMouseDown);
    this.renderer.domElement.addEventListener('mousemove', this.handleMouseMove);
    this.renderer.domElement.addEventListener('mouseup', this.handleMouseUp);
    this.renderer.domElement.addEventListener('wheel', this.handleWheel);

    this.updateCameraPosition();
  }

  private handleResize = (): void => {
    if (this.container && this.camera && this.renderer) {
      const width = this.container.clientWidth;
      const height = this.container.clientHeight;

      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    }
  };

  private handleMouseDown = (event: MouseEvent): void => {
    if (event.button === 1) {
      this.isDragging = true;
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      event.preventDefault();
    }
  };

  private handleMouseMove = (event: MouseEvent): void => {
    if (this.isDragging) {
      const deltaX = event.clientX - this.lastMouseX;
      const deltaY = event.clientY - this.lastMouseY;

      this.cameraAngle -= deltaX * 0.01;
      this.cameraHeight = Math.max(20, Math.min(200, this.cameraHeight - deltaY * 0.5));

      this.updateCameraPosition();

      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
    }
  };

  private handleMouseUp = (): void => {
    this.isDragging = false;
  };

  private handleWheel = (event: WheelEvent): void => {
    event.preventDefault();
    this.cameraDistance = Math.max(50, Math.min(300, this.cameraDistance + event.deltaY * 0.1));
    this.updateCameraPosition();
  };

  private updateCameraPosition(): void {
    if (!this.camera) return;

    const centerX = this.gridWidth / 2;
    const centerY = this.gridHeight / 2;

    this.camera.position.x = centerX + Math.cos(this.cameraAngle) * this.cameraDistance;
    this.camera.position.y = this.cameraHeight;
    this.camera.position.z = centerY + Math.sin(this.cameraAngle) * this.cameraDistance;

    this.camera.lookAt(centerX, 0, centerY);
  }

  public resize(width: number, height: number): void {
    if (this.camera && this.renderer) {
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    }
  }

  public render(state: SimulationState): void {
    if (!this.scene || !this.camera || !this.renderer) return;

    const { sandGrid, waterGrid, metadata } = state;
    const { gridWidth, gridHeight } = metadata;

    if (this.gridWidth !== gridWidth || this.gridHeight !== gridHeight) {
      this.gridWidth = gridWidth;
      this.gridHeight = gridHeight;
      this.updateCameraPosition();
    }

    if (this.sandMesh) {
      this.scene.remove(this.sandMesh);
      this.sandMesh.geometry.dispose();
    }

    // Clean up old water meshes
    for (const mesh of this.waterMeshes) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
    }
    this.waterMeshes = [];

    if (this.sidePlanes) {
      this.scene.remove(this.sidePlanes);
      this.sidePlanes.children.forEach((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) {
            child.material.dispose();
          }
        }
      });
    }

    this.sandMesh = this.createSandMesh(sandGrid);
    this.scene.add(this.sandMesh);

    if (waterGrid) {
      // Create separate meshes for each body of water
      this.waterMeshes = this.createWaterMeshes(sandGrid, waterGrid);
      for (const mesh of this.waterMeshes) {
        this.scene.add(mesh);
      }

      this.sidePlanes = this.createSidePlanes(sandGrid, waterGrid);
      this.scene.add(this.sidePlanes);
    }

    this.renderer.render(this.scene, this.camera);
  }

  private createSandMesh(sandGrid: any): THREE.Mesh {
    const geometry = new THREE.PlaneGeometry(
      this.gridWidth,
      this.gridHeight,
      this.gridWidth - 1,
      this.gridHeight - 1
    );

    const positions = geometry.attributes.position;
    const colors = [];

    for (let y = 0; y < this.gridHeight; y++) {
      for (let x = 0; x < this.gridWidth; x++) {
        const height = sandGrid.get(x, y);
        const idx = y * this.gridWidth + x;
        positions.setZ(idx, height);

        // Realistic sand colors with imperfections
        const normalizedHeight = Math.min(height / 20, 1);

        // Add color variation for natural look
        const variation = Math.sin(x * 0.3) * Math.cos(y * 0.4) * 0.05 +
                         Math.sin(x * 1.1 + y * 0.7) * 0.03;

        // Light sand: (240, 220, 180), Dark sand: (200, 180, 130)
        const r = Math.max(0, Math.min(1, (240 - 40 * normalizedHeight) / 255 + variation));
        const g = Math.max(0, Math.min(1, (220 - 40 * normalizedHeight) / 255 + variation));
        const b = Math.max(0, Math.min(1, (180 - 50 * normalizedHeight) / 255 + variation * 0.5));

        colors.push(r, g, b);
      }
    }

    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.computeVertexNormals();
    geometry.rotateX(-Math.PI / 2);
    geometry.translate(this.gridWidth / 2, 0, this.gridHeight / 2);

    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 1.0,  // Sand is very rough/matte
      metalness: 0.0   // Sand is not metallic
    });

    return new THREE.Mesh(geometry, material);
  }

  // Flood fill to detect separate water bodies
  private floodFillWaterBodies(waterGrid: any): number[][] {
    const visited = new Array(this.gridHeight)
      .fill(0)
      .map(() => new Array(this.gridWidth).fill(false));

    const waterBodies: number[][] = [];

    const floodFill = (startX: number, startY: number): number[] => {
      const cells: number[] = [];
      const queue: [number, number][] = [[startX, startY]];
      visited[startY][startX] = true;

      while (queue.length > 0) {
        const [x, y] = queue.shift()!;
        cells.push(x, y);

        // Check 4 neighbors
        const neighbors = [
          [x - 1, y], [x + 1, y],
          [x, y - 1], [x, y + 1]
        ];

        for (const [nx, ny] of neighbors) {
          if (nx >= 0 && nx < this.gridWidth &&
              ny >= 0 && ny < this.gridHeight &&
              !visited[ny][nx]) {
            const waterDepth = waterGrid.getHeight(nx, ny);
            if (waterDepth > 0.01) { // Has water
              visited[ny][nx] = true;
              queue.push([nx, ny]);
            }
          }
        }
      }

      return cells;
    };

    // Find all water bodies
    for (let y = 0; y < this.gridHeight; y++) {
      for (let x = 0; x < this.gridWidth; x++) {
        const waterDepth = waterGrid.getHeight(x, y);
        if (waterDepth > 0.01 && !visited[y][x]) {
          const body = floodFill(x, y);
          if (body.length > 0) {
            waterBodies.push(body);
          }
        }
      }
    }

    return waterBodies;
  }

  private createWaterMeshes(sandGrid: any, waterGrid: any): THREE.Mesh[] {
    const waterBodies = this.floodFillWaterBodies(waterGrid);
    const meshes: THREE.Mesh[] = [];

    for (const body of waterBodies) {
      // Create a set of water cells for quick lookup
      const waterCells = new Set<string>();
      for (let i = 0; i < body.length; i += 2) {
        const x = body[i];
        const y = body[i + 1];
        waterCells.add(`${x},${y}`);
      }

      // Build mesh for this water body
      const positions: number[] = [];
      const colors: number[] = [];
      const indices: number[] = [];
      const vertexMap = new Map<string, number>();

      let vertexIndex = 0;

      // Create vertices and faces for water cells
      for (let i = 0; i < body.length; i += 2) {
        const x = body[i];
        const y = body[i + 1];

        const sandHeight = sandGrid.get(x, y);
        const waterDepth = waterGrid.getHeight(x, y);
        const waterSurfaceHeight = sandHeight + waterDepth;

        // Normalize depth for coloring
        const normalizedDepth = Math.min(waterDepth / 10, 1);

        // Add color variation for natural look
        const variation = Math.sin(x * 0.5 + y * 0.3) * 0.02 +
                         Math.cos(x * 0.7 - y * 0.5) * 0.015;

        // Turquoise to Deep blue
        const r = Math.max(0, Math.min(1, (100 + (20 - 100) * normalizedDepth) / 255 + variation));
        const g = Math.max(0, Math.min(1, (200 + (120 - 200) * normalizedDepth) / 255 + variation));
        const b = Math.max(0, Math.min(1, (220 + (180 - 220) * normalizedDepth) / 255 + variation * 0.5));

        // Create quad for this cell (4 vertices)
        const corners = [
          [x, y], [x + 1, y], [x + 1, y + 1], [x, y + 1]
        ];

        const cornerIndices: number[] = [];
        for (const [cx, cy] of corners) {
          const key = `${cx},${cy}`;
          if (!vertexMap.has(key)) {
            vertexMap.set(key, vertexIndex);
            cornerIndices.push(vertexIndex);

            // Position (in world space, rotated for Three.js)
            positions.push(
              cx - this.gridWidth / 2,
              waterSurfaceHeight,
              cy - this.gridHeight / 2
            );

            colors.push(r, g, b);
            vertexIndex++;
          } else {
            cornerIndices.push(vertexMap.get(key)!);
          }
        }

        // Two triangles for the quad
        indices.push(
          cornerIndices[0], cornerIndices[1], cornerIndices[2],
          cornerIndices[0], cornerIndices[2], cornerIndices[3]
        );
      }

      if (positions.length === 0) continue;

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      geometry.setIndex(indices);
      geometry.computeVertexNormals();

      const material = new THREE.MeshStandardMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 0.7,
        roughness: 0.2,
        metalness: 0.3,
        side: THREE.DoubleSide
      });

      meshes.push(new THREE.Mesh(geometry, material));
    }

    return meshes;
  }

  private createSidePlanes(sandGrid: any, waterGrid: any): THREE.Group {
    const group = new THREE.Group();
    const minHeight = -10; // Bottom of the side planes

    // Helper to create a vertical quad strip along an edge
    const createEdgePlane = (
      positions: number[],
      colors: number[],
      isXAxis: boolean,
      fixedCoord: number
    ) => {
      const length = isXAxis ? this.gridWidth : this.gridHeight;

      for (let i = 0; i < length; i++) {
        const x = isXAxis ? i : fixedCoord;
        const y = isXAxis ? fixedCoord : i;

        const sandHeight = sandGrid.get(x, y);
        const waterDepth = waterGrid.getHeight(x, y); // Water depth above sand

        // Calculate water surface position in world space
        const waterTop = sandHeight + waterDepth;

        // World coordinates for this edge position
        const worldX = isXAxis ? i : fixedCoord;
        const worldZ = isXAxis ? fixedCoord : i;

        // Create quad from minHeight to sandHeight (sand portion)
        const x0 = worldX;
        const x1 = worldX + (isXAxis ? 1 : 0);
        const z0 = worldZ;
        const z1 = worldZ + (isXAxis ? 0 : 1);

        // Sand quad
        const sandColor = this.getSandColor(sandHeight);
        this.addVerticalQuad(positions, colors,
          x0, z0, x1, z1, minHeight, sandHeight,
          sandColor[0], sandColor[1], sandColor[2]);

        // Water quad (if there's water above sand)
        if (waterTop > sandHeight + 0.01) {
          const waterDepth = waterTop - Math.min(sandHeight, 0);
          const waterColor = this.getWaterColor(waterDepth);
          this.addVerticalQuad(positions, colors,
            x0, z0, x1, z1, sandHeight, waterTop,
            waterColor[0], waterColor[1], waterColor[2]);
        }
      }
    };

    // Create geometry for all 4 sides
    const positions: number[] = [];
    const colors: number[] = [];

    // Front edge (z=0)
    createEdgePlane(positions, colors, true, 0);

    // Back edge (z=height-1)
    createEdgePlane(positions, colors, true, this.gridHeight - 1);

    // Left edge (x=0)
    createEdgePlane(positions, colors, false, 0);

    // Right edge (x=width-1)
    createEdgePlane(positions, colors, false, this.gridWidth - 1);

    // Create mesh from geometry
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.computeVertexNormals();

    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      side: THREE.DoubleSide,
      roughness: 0.8,
      metalness: 0.1
    });

    const mesh = new THREE.Mesh(geometry, material);
    // Don't translate - coordinates are already in world space to match sand/water
    group.add(mesh);

    return group;
  }

  private addVerticalQuad(
    positions: number[],
    colors: number[],
    x0: number,
    z0: number,
    x1: number,
    z1: number,
    yBottom: number,
    yTop: number,
    r: number,
    g: number,
    b: number
  ): void {
    // Two triangles forming a quad
    // Triangle 1: bottom-left, bottom-right, top-right
    positions.push(x0, yBottom, z0);
    positions.push(x1, yBottom, z1);
    positions.push(x1, yTop, z1);

    colors.push(r, g, b);
    colors.push(r, g, b);
    colors.push(r, g, b);

    // Triangle 2: bottom-left, top-right, top-left
    positions.push(x0, yBottom, z0);
    positions.push(x1, yTop, z1);
    positions.push(x0, yTop, z0);

    colors.push(r, g, b);
    colors.push(r, g, b);
    colors.push(r, g, b);
  }

  private getSandColor(height: number): [number, number, number] {
    const normalizedHeight = Math.min(height / 20, 1);
    const r = Math.max(0, Math.min(1, (240 - 40 * normalizedHeight) / 255));
    const g = Math.max(0, Math.min(1, (220 - 40 * normalizedHeight) / 255));
    const b = Math.max(0, Math.min(1, (180 - 50 * normalizedHeight) / 255));
    return [r * 0.85, g * 0.85, b * 0.85]; // Slightly darker for sides
  }

  private getWaterColor(depth: number): [number, number, number] {
    const normalizedDepth = Math.min(depth / 10, 1);
    const r = (100 + (20 - 100) * normalizedDepth) / 255;
    const g = (200 + (120 - 200) * normalizedDepth) / 255;
    const b = (220 + (180 - 220) * normalizedDepth) / 255;
    return [r * 0.9, g * 0.9, b * 0.9]; // Slightly darker for sides
  }

  public screenToWorld(screenX: number, screenY: number): Coordinate | null {
    if (!this.camera || !this.renderer) return null;

    const rect = this.renderer.domElement.getBoundingClientRect();
    const x = ((screenX - rect.left) / rect.width) * 2 - 1;
    const y = -((screenY - rect.top) / rect.height) * 2 + 1;

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(x, y), this.camera);

    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const intersectPoint = new THREE.Vector3();
    raycaster.ray.intersectPlane(plane, intersectPoint);

    if (intersectPoint) {
      return {
        x: Math.floor(intersectPoint.x),
        y: Math.floor(intersectPoint.z)
      };
    }

    return null;
  }

  public pan(deltaX: number, deltaY: number): void {
    // For 3D renderer, pan rotates the camera angle and adjusts height
    this.cameraAngle -= deltaX * 0.02;
    this.cameraHeight = Math.max(20, Math.min(200, this.cameraHeight - deltaY * 2));
    this.updateCameraPosition();
  }

  public zoom(delta: number): void {
    // Zoom adjusts camera distance
    this.cameraDistance = Math.max(50, Math.min(300, this.cameraDistance + delta * 10));
    this.updateCameraPosition();
  }

  public drawHoverPreview(worldCoord: Coordinate, brushSize: number): void {
    // For 3D renderer, hover preview would require adding/removing meshes
    // which is more complex. Skip for now as the brush effect is visible in 2D renderers
  }

  public dispose(): void {
    window.removeEventListener('resize', this.handleResize);

    if (this.renderer && this.renderer.domElement) {
      this.renderer.domElement.removeEventListener('mousedown', this.handleMouseDown);
      this.renderer.domElement.removeEventListener('mousemove', this.handleMouseMove);
      this.renderer.domElement.removeEventListener('mouseup', this.handleMouseUp);
      this.renderer.domElement.removeEventListener('wheel', this.handleWheel);
    }

    if (this.sandMesh) {
      this.sandMesh.geometry.dispose();
      if (this.sandMesh.material instanceof THREE.Material) {
        this.sandMesh.material.dispose();
      }
    }

    // Clean up water meshes
    for (const mesh of this.waterMeshes) {
      mesh.geometry.dispose();
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
    }

    if (this.sidePlanes) {
      this.sidePlanes.children.forEach((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) {
            child.material.dispose();
          }
        }
      });
    }

    if (this.renderer && this.container) {
      this.container.removeChild(this.renderer.domElement);
      this.renderer.dispose();
    }

    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.sandMesh = null;
    this.waterMeshes = [];
    this.sidePlanes = null;
    this.container = null;
  }
}
